import { FC, useMemo, useState } from "react";
import { TableCell, TableRow } from "../ui/table";
import { formatDate } from "../../lib/helpers/date_helpers";
import { Button } from "../ui/button";
import { Download } from "lucide-react";
import saveAs from "file-saver";
import { toast } from "sonner";
import { Abonnement } from "../../lib/interfaces/abonnement.interface";
import { User } from "../../lib/interfaces/user.interface";
import DemanderSiretDialog from "../dialogs/DemanderSiretDialog";
import { CODE_SIRET_MANQUANT, getCodeErreurMetier } from "@/lib/helpers/erreurs_helpers";
import { useAttestationCiteoFile, useAttestationLekoFile } from "@/lib/hooks/useAbonnements";

interface Props {
  abonnement: Abonnement;
  user?: User;
}

const AttestationCiteoRowItem: FC<Props> = ({ abonnement, user }) => {
  const afficherCiteo = useMemo(() => abonnement.dateDebut <= "2024-12-01", [abonnement]);
  const afficherLeko = useMemo(() => abonnement.dateFin > "2025-01-01", [abonnement]);
  const { mutateAsync: getAttestationCiteoFile, isPending: loadingAttestationCiteo } = useAttestationCiteoFile();
  const { mutateAsync: getAttestationLekoFile, isPending: loadingAttestationLeko } = useAttestationLekoFile();
  const loadingAttestation = loadingAttestationLeko || loadingAttestationCiteo;
  const facturesImpayees = abonnement.factures.filter((fac) => fac.payee === false);
  const [openSiretDialog, setOpenSiretDialog] = useState(false);

  const telechargerAttestationCiteo = async () => {
    if (!user) return;
    try {
      const data = await getAttestationCiteoFile(abonnement.id);
      const blob = new Blob([data], {
        type: "application/pdf",
      });
      saveAs(blob, `attestation-citeo.pdf`);
    } catch (error) {
      toast.error("Une erreur s'est produite.");
      console.error(error);
    }
  };

  const telechargerAttestationLeko = async (siret?: string) => {
    if (!user) return;
    // Demander le SIRET
    if (!user.siret && !siret) {
      setOpenSiretDialog(true);
      return;
    }

    try {
      const data = await getAttestationLekoFile(abonnement.id);
      const blob = new Blob([data], {
        type: "application/pdf",
      });
      saveAs(blob, `attestation-eco-contribution.pdf`);
    } catch (error) {
      // Le back considère le SIRET comme manquant alors que le profil en cache en contient un
      // (valeur vide ou invalide) : on le redemande au lieu d'afficher une erreur générique,
      // sinon l'utilisateur reste bloqué sur un téléchargement qui échouera indéfiniment.
      if (getCodeErreurMetier(error) === CODE_SIRET_MANQUANT) {
        setOpenSiretDialog(true);
        return;
      }
      toast.error("Une erreur s'est produite.");
      console.error(error);
    }
  };

  return (
    <>
      {afficherLeko && (
        <TableRow className='block px-4 py-3 hover:bg-slate-50/70 sm:table-row sm:px-0 sm:py-0'>
          <TableCell className='block px-0 pb-2 pt-0 font-semibold text-slate-900 sm:table-cell sm:px-6 sm:py-4'>
            Éco-contribution LEKO
          </TableCell>
          <TableCell className='flex items-center justify-between gap-4 px-0 py-1.5 text-sm text-slate-600 sm:table-cell sm:p-4'>
            <span className='font-medium text-slate-500 sm:hidden'>Validité</span>
            <span>
              Du {new Date(abonnement.dateDebut).getFullYear() === 2024 ? "01/01/2025" : formatDate(abonnement.dateDebut)}{" "}
              au {formatDate(abonnement.dateFin)}
            </span>
          </TableCell>
          <TableCell className='flex items-center justify-between gap-4 px-0 py-1.5 text-sm text-slate-600 sm:table-cell sm:p-4'>
            <span className='font-medium text-slate-500 sm:hidden'>Ruches couvertes</span>
            <span>{abonnement.ruches}</span>
          </TableCell>
          <TableCell className='block px-0 pb-0 pt-3 text-right sm:table-cell sm:px-6 sm:py-4'>
            {facturesImpayees.length === 0 ? (
              <Button
                className='w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto'
                onClick={() => telechargerAttestationLeko()}
                loading={loadingAttestation}
                icon={<Download className='h-4 w-4' />}
                disabled={!user}
                size='sm'
              >
                Télécharger
              </Button>
            ) : (
              <div className='inline-flex w-full justify-center rounded-full bg-[#fff6d8] px-3 py-1.5 text-xs font-semibold text-[#725716] sm:w-auto'>
                En attente de paiement
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
      {afficherCiteo && (
        <TableRow className='block px-4 py-3 hover:bg-slate-50/70 sm:table-row sm:px-0 sm:py-0'>
          <TableCell className='block px-0 pb-2 pt-0 font-semibold text-slate-900 sm:table-cell sm:px-6 sm:py-4'>
            Éco-contribution CITEO
          </TableCell>
          <TableCell className='flex items-center justify-between gap-4 px-0 py-1.5 text-sm text-slate-600 sm:table-cell sm:p-4'>
            <span className='font-medium text-slate-500 sm:hidden'>Validité</span>
            <span>
              Du {formatDate(abonnement.dateDebut)} au{" "}
              {new Date(abonnement.dateDebut).getFullYear() === 2024 ? "31/12/2024" : formatDate(abonnement.dateFin)}
            </span>
          </TableCell>
          <TableCell className='flex items-center justify-between gap-4 px-0 py-1.5 text-sm text-slate-600 sm:table-cell sm:p-4'>
            <span className='font-medium text-slate-500 sm:hidden'>Ruches couvertes</span>
            <span>{abonnement.ruches}</span>
          </TableCell>
          <TableCell className='block px-0 pb-0 pt-3 text-right sm:table-cell sm:px-6 sm:py-4'>
            {facturesImpayees.length === 0 ? (
              <Button
                className='w-full bg-[#173f35] hover:bg-[#23594a] sm:w-auto'
                onClick={telechargerAttestationCiteo}
                loading={loadingAttestation}
                icon={<Download className='h-4 w-4' />}
                disabled={!user}
                size='sm'
              >
                Télécharger
              </Button>
            ) : (
              <div className='inline-flex w-full justify-center rounded-full bg-[#fff6d8] px-3 py-1.5 text-xs font-semibold text-[#725716] sm:w-auto'>
                En attente de paiement
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
      <DemanderSiretDialog
        userId={user?.id ?? ""}
        open={openSiretDialog}
        setOpen={setOpenSiretDialog}
        onSubmit={(siret) => telechargerAttestationLeko(siret)}
      />
    </>
  );
};

export default AttestationCiteoRowItem;
